/**
 * sticky-atc.js
 * Manages the fixed Add-to-Cart card that appears when the primary buy button
 * scrolls out of view on product pages.
 *
 * All configuration is read from data-* attributes on the bar element so this
 * file contains no Liquid and is fully cacheable by Shopify's CDN.
 *
 * Data attributes expected on the .sticky-atc-bar element:
 *   data-section-id     — section.id (scopes all DOM lookups)
 *   data-form-id        — id of the product form to proxy clicks to
 *   data-trigger-id     — id of the element to observe (buy buttons wrapper)
 *   data-qty-id         — id of the main page quantity input
 *   data-add-label      — "Add to cart" translated string
 *   data-sold-out-label — "Sold out" translated string
 */

(function () {
  function initStickyATC(bar) {
    var sectionId    = bar.dataset.sectionId;
    var formId       = bar.dataset.formId;
    var triggerId    = bar.dataset.triggerId;
    var qtyId        = bar.dataset.qtyId;
    var addLabel     = bar.dataset.addLabel;
    var soldOutLabel = bar.dataset.soldOutLabel;

    var trigger      = document.getElementById(triggerId);
    var stickyBtn    = bar.querySelector('.sticky-atc-bar__btn');
    var stickyLabel  = bar.querySelector('.sticky-atc-bar__btn-label');
    var stickySpinner = bar.querySelector('.sticky-atc-bar__btn .loading-overlay__spinner');
    var minimizeBtn  = bar.querySelector('.sticky-atc-bar__minimize');
    var closeBtns    = bar.querySelectorAll('.sticky-atc-bar__close');
    var fab          = bar.querySelector('.sticky-atc-bar__fab');
    var stickyQtyInput = bar.querySelector('.sticky-atc-bar__qty-input');
    var pulsed       = false;

    if (!trigger || !stickyBtn) return;

    // ── IntersectionObserver ────────────────────────────────────────────────
    var observer = new IntersectionObserver(function (entries) {
      var entry = entries[0];

      if (!entry.isIntersecting && !bar.classList.contains('is-dismissed')) {
        bar.classList.add('is-visible');
        bar.setAttribute('aria-hidden', 'false');

        if (!pulsed) {
          pulsed = true;
          bar.classList.add('pulse-once');
          bar.addEventListener('animationend', function () {
            bar.classList.remove('pulse-once');
          }, { once: true });
        }
      } else {
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
      }
    }, { threshold: 0, rootMargin: '0px' });

    observer.observe(trigger);

    // ── Minimize / Expand ───────────────────────────────────────────────────
    if (minimizeBtn) {
      minimizeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        bar.classList.add('is-minimized');
        bar.setAttribute('aria-label', 'Expand Add to Cart');
      });
    }

    // Close buttons: permanently dismiss the card for this page visit
    closeBtns.forEach(function (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        observer.unobserve(trigger);
        bar.classList.add('is-dismissed');
      });
    });

    // Clicking the FAB or the minimized card expands the card
    function expandCard() {
      bar.classList.remove('is-minimized');
      bar.removeAttribute('aria-label');
    }

    if (fab) {
      fab.addEventListener('click', function (e) {
        e.stopPropagation();
        expandCard();
      });
    }

    // Clicking anywhere on the minimized card also expands it
    bar.addEventListener('click', function () {
      if (bar.classList.contains('is-minimized')) {
        expandCard();
      }
    });

    // ── ATC button: proxy click to real form submit ─────────────────────────
    stickyBtn.addEventListener('click', function (e) {
      e.stopPropagation(); // prevent the bar-level click handler from firing
      var submitBtn = document.querySelector(
        '#' + formId + ' [type="submit"][name="add"]'
      );
      if (
        submitBtn &&
        !submitBtn.disabled &&
        submitBtn.getAttribute('aria-disabled') !== 'true'
      ) {
        submitBtn.click();
      }
    });

    // ── MutationObserver: sync disabled state ───────────────────────────────
    var origSubmit = document.querySelector(
      '#' + formId + ' [type="submit"][name="add"]'
    );
    if (origSubmit) {
      new MutationObserver(function () {
        var isDisabled =
          origSubmit.hasAttribute('disabled') ||
          origSubmit.getAttribute('aria-disabled') === 'true';
        stickyBtn.disabled = isDisabled;
        // Mirror aria-disabled so the CSS [aria-disabled="true"] selector works
        if (isDisabled) {
          stickyBtn.setAttribute('aria-disabled', 'true');
        } else {
          stickyBtn.removeAttribute('aria-disabled');
        }
      }).observe(origSubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });

      // Mirror spinner visibility
      var origSpinner = origSubmit.querySelector('.loading-overlay__spinner');
      if (origSpinner && stickySpinner) {
        new MutationObserver(function () {
          stickySpinner.classList.toggle(
            'hidden',
            origSpinner.classList.contains('hidden')
          );
        }).observe(origSpinner, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }

    // ── Case price × quantity ───────────────────────────────────────────────
    // The .rmoney_price span contains "Case price: $275.13".
    // We extract the base per-case price once, then multiply by qty on every change.
    var caseRpriceEl  = bar.querySelector('.sticky-atc-bar__price .rmoney_price');
    var casePriceNode = null; // the specific text node holding the dollar amount
    var baseCasePrice = null;

    function parseCasePrice() {
      if (!caseRpriceEl) return;
      // Walk child nodes from the end to find the last non-empty text node (the price)
      var nodes = caseRpriceEl.childNodes;
      for (var i = nodes.length - 1; i >= 0; i--) {
        var n = nodes[i];
        if (n.nodeType === 3 && n.textContent.trim()) { // TEXT_NODE
          var numMatch = n.textContent.match(/[\d,]+\.?\d*/);
          if (numMatch) {
            baseCasePrice = parseFloat(numMatch[0].replace(/,/g, ''));
            casePriceNode = n;
          }
          break;
        }
      }
    }

    function updateCasePrice(qty) {
      if (!casePriceNode || baseCasePrice === null) return;
      var total     = baseCasePrice * qty;
      var formatted = total.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      // Replace only the numeric portion, preserving the currency symbol + surrounding whitespace
      casePriceNode.textContent = casePriceNode.textContent.replace(/[\d,]+\.?\d*/, formatted);
    }

    parseCasePrice();

    // ── Quantity stepper ────────────────────────────────────────────────────
    var mainQtyInput = document.getElementById(qtyId);

    if (mainQtyInput && stickyQtyInput) {
      // Initialise sticky qty from the main input's current value
      stickyQtyInput.value = mainQtyInput.value || 1;
      updateCasePrice(parseInt(stickyQtyInput.value, 10) || 1);

      // Sticky qty → main qty
      stickyQtyInput.addEventListener('change', function () {
        var val = Math.max(1, parseInt(stickyQtyInput.value, 10) || 1);
        stickyQtyInput.value = val;
        mainQtyInput.value   = val;
        updateCasePrice(val);
        mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Main qty → sticky qty (e.g. user changes it on the product form directly)
      mainQtyInput.addEventListener('change', function () {
        var val = Math.max(1, parseInt(mainQtyInput.value, 10) || 1);
        stickyQtyInput.value = val;
        updateCasePrice(val);
      });
    }

    // +/- buttons
    bar.querySelectorAll('.sticky-atc-bar__qty-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var current = parseInt(stickyQtyInput ? stickyQtyInput.value : 1, 10) || 1;
        var next    = btn.dataset.action === 'plus' ? current + 1 : Math.max(1, current - 1);
        if (stickyQtyInput) stickyQtyInput.value = next;
        if (mainQtyInput) {
          mainQtyInput.value = next;
          mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        updateCasePrice(next);
      });
    });
  }

  document.querySelectorAll('.sticky-atc-bar[data-section-id]').forEach(initStickyATC);
})();
